import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Mail, ArrowRight, ShieldCheck, Ticket } from "lucide-react";
import storeToken from "../utils/storeToken";
import Otp from "./Otp";

function Auth({
  isOpen,
  setToken,
  setCustomerDetails,
  setupDetails,
  setView,
  setActiveIcon,
  widgetConfigs,
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: widgetConfigs?.userEmail || widgetConfigs?.email || "",
    phone: "",
  });
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [imageError, setImageError] = useState(false);

  // Brand colors from setupDetails
  const primaryColor =
    setupDetails?.widgetCustomizations?.primaryColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#2563eb";
  const secondaryColor =
    setupDetails?.widgetCustomizations?.secondaryColor ||
    setupDetails?.widgetCustomizations?.color2 ||
    "#3b82f6";
  const backgroundColor =
    setupDetails?.widgetCustomizations?.backgroundColor || "#FFFFFF";
  const partnerName =
    setupDetails?.widgetCustomizations?.partnerName || "Support";

  const activeLoginLogo =
    setupDetails?.widgetCustomizations?.loginLogoUrl ||
    setupDetails?.widgetCustomizations?.logoUrl;

  const primaryShade = (opacity) => {
    const hex = primaryColor.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return `rgba(37, 99, 235, ${opacity})`;
  };

  const submit = useCallback(
    async (emailToSubmit) => {
      const email = (emailToSubmit || formData.email).trim();
      if (!email) {
        setErrorMessage("Please enter your email address.");
        setLoading(false);
        return;
      }

      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErrorMessage("Please enter a valid email address.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_AUTH_MICROSERVICE_URL}/customers/create`,
          { email, uniquePartnerId: widgetConfigs?.uniquePartnerId }
        );

        if (response.status === 200 || response.status === 201) {
          setShowOtp(false);
          setView("chatList");
          setCustomerDetails({
            createdAt: response.data.customer?.createdAt,
            email: response.data.customer?.email,
            status: response.data.customer?.status,
            updatedAt: response.data.customer?.updatedAt,
            _id: response.data.customer?._id,
          });

          storeToken(response.data.accessToken);
          setToken(localStorage.getItem("dexkor-chat-widget-auth-token"));
          localStorage.setItem(
            "dexkor-chat-widget-customer-details",
            JSON.stringify(response.data.customer)
          );

          if (emailToSubmit) {
            setCustomerDetails({
              _id: widgetConfigs.userId,
              email: widgetConfigs.userEmail || widgetConfigs.email,
            });
          }
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error("There was an error identifying the customer!", error);
        setErrorMessage(
          error.response?.data?.message ||
            "Unable to connect with this email. Please try again."
        );
      }
    },
    [formData.email, widgetConfigs, setCustomerDetails, setToken, setView]
  );

  // --- AUTOMATIC SUBMISSION LOGIC ---
  const autoEmail = widgetConfigs?.userEmail || widgetConfigs?.email;
  useEffect(() => {
    if (autoEmail && isOpen) {
      setLoading(true);
      submit(autoEmail);
    }
  }, [autoEmail, isOpen, submit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (errorMessage) setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    submit();
  };

  return (
    <>
      {isOpen && !showOtp && (
        <div
          className="flex-1 flex flex-col justify-center px-5 py-6 min-h-full transition-colors animate-in fade-in duration-200"
          style={{ backgroundColor }}
        >
          <div className="w-full max-w-sm mx-auto">
            {/* Enterprise Partner Icon / Badge */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border transition-all"
              style={{
                backgroundColor: primaryShade(0.08),
                borderColor: primaryShade(0.2),
              }}
            >
              {activeLoginLogo && !imageError ? (
                <img
                  src={activeLoginLogo}
                  alt={partnerName}
                  className="w-9 h-9 object-contain rounded-xl"
                  onError={() => setImageError(true)}
                />
              ) : (
                <Ticket
                  className="w-7 h-7 stroke-[1.8]"
                  style={{ color: primaryColor }}
                />
              )}
            </div>

            {/* Clean, Non-overlapping Headings */}
            <div className="text-center px-2">
              <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-snug break-words">
                {setupDetails?.widgetCustomizations?.loginHeading ||
                  `Welcome to ${partnerName}`}
              </h2>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed break-words">
                {setupDetails?.widgetCustomizations?.loginDescription ||
                  "Please enter your email to view your support tickets and track your conversations."}
              </p>
            </div>

            {/* Enterprise Form Card */}
            <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="auth-email"
                    className="flex items-center justify-between text-xs font-semibold text-slate-700 mb-1.5"
                  >
                    <span>Email address</span>
                    <span className="text-[10px] text-slate-400 font-normal">
                      Required
                    </span>
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-600 transition-colors">
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      id="auth-email"
                      type="email"
                      name="email"
                      placeholder="example@example.com"
                      disabled={Boolean(widgetConfigs?.userEmail || widgetConfigs?.email)}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50/70 hover:bg-slate-50 focus:bg-white text-xs font-medium text-slate-900 placeholder-slate-400 rounded-xl border border-slate-200 focus:border-transparent focus:outline-none focus:ring-2 transition-all"
                      style={{
                        "--tw-ring-color": primaryColor,
                      }}
                    />
                  </div>
                  {errorMessage && (
                    <div className="text-[11px] text-rose-500 mt-1.5 font-medium flex items-center gap-1 animate-in fade-in">
                      <span>•</span>
                      <span>{errorMessage}</span>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-95 hover:shadow-md active:scale-[0.99] disabled:opacity-60 border-none outline-none shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    boxShadow: `0 4px 14px ${primaryShade(0.28)}`,
                  }}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    <>
                      <span>Continue to Tickets</span>
                      <ArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
                    </>
                  )}
                </button>
              </form>

              {/* Security note */}
              <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 text-center">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>Your information is protected & secure</span>
              </div>
            </div>

            {/* Back to Home action */}
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => {
                  setView("LandingWindow");
                  setActiveIcon("home");
                }}
                className="text-xs text-slate-500 hover:text-slate-800 transition-colors font-medium border-none bg-transparent cursor-pointer"
              >
                ← Return to Home
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtp && (
        <Otp
          customerData={formData}
          setShowOtp={setShowOtp}
          setToken={setToken}
          setCustomerDetails={setCustomerDetails}
          resendOTP={submit}
          setupDetails={setupDetails}
          setView={setView}
        />
      )}
    </>
  );
}

export default Auth;
